import { watch, type Ref } from 'vue'
import * as Y from 'yjs'
import { useYArrayView } from './repositories-sync'
import { beginPersist, endPersist, syncConnected } from './sync-status'
import type { Bookmark, Highlight, HighlightComment, Summary, Translation } from '@/types/domain'
import type { SyncedDoc } from './yjs-sync'

type DocContext = { doc: Y.Doc; ready: Promise<void> }

/** Debounce para persistir cambios remotos en IndexedDB (evita escrituras por micro-lote del CRDT). */
const PERSIST_REMOTE_DEBOUNCE_MS = 800

/**
 * Conecta un store (array local) con su Y.Array sincronizado.
 *
 * Sincronización bidireccional completa (altas, bajas, modificaciones)
 * SIN bucle infinito.
 *
 * Anti-bucle: usamos un guard `applying` que se mantiene activo
 * durante toda la propagación asíncrona. Cuando mutamos un lado
 * como consecuencia de un cambio del otro, fijamos applying=true;
 * los watchers comprueban applying al inicio y saltan si está activo.
 * Como las mutaciones se completan antes de que apply se resetee
 * (lo hacemos en un microtask), no hay reentrada.
 *
 * Además, antes de mutar comparamos los conjuntos de ids: si coinciden,
 * no hacemos nada (idempotencia).
 */
export async function connectCollection<T extends { id: string }>(
  localItems: Ref<T[]>,
  ctx: DocContext,
  key: string,
  onRemoteChange?: (items: T[]) => Promise<void>,
): Promise<{ disconnect: () => void }> {
  const yarray = ctx.doc.getArray<T>(key)
  const view = useYArrayView<T>(yarray)

  let applying = false
  let persistTimer: ReturnType<typeof setTimeout> | null = null

  /**
   * Persiste el estado reconciliado en IndexedDB, de forma debounced.
   * Solo se invoca cuando un cambio remoto se aplicó al local (origen
   * del CRDT, no acción del usuario local). El anti-bucle lo garantiza
   * el guard `applying` en `syncRemoteToLocal`.
   */
  function scheduleRemotePersist() {
    if (!onRemoteChange) return
    if (persistTimer) clearTimeout(persistTimer)
    beginPersist()
    persistTimer = setTimeout(async () => {
      try {
        await onRemoteChange(localItems.value.slice())
      } catch (e) {
        console.warn(`[sync] persistencia local de "${key}" falló:`, e)
      } finally {
        endPersist()
      }
    }, PERSIST_REMOTE_DEBOUNCE_MS)
  }

  /** Sincroniza local → Y.Array: añade nuevos, borra quitados, actualiza modificados. */
  function syncLocalToRemote(newItems: T[]) {
    if (applying) return
    const remoteIds = new Set(view.items.value.map((it) => it.id))
    const localIds = new Set(newItems.map((it) => it.id))

    const toAdd = newItems.filter((it) => !remoteIds.has(it.id))
    const toRemove = view.items.value.filter((it) => !localIds.has(it.id))
    // Modificados: mismo id pero contenido distinto. Para entidades
    // inmutables (highlights/summaries...) esta rama nunca se dispara;
    // para entidades editables (p.ej. estanterías) propaga la edición.
    const remoteMap = new Map(view.items.value.map((it) => [it.id, it]))
    const toUpdate = newItems.filter(
      (it) => remoteMap.has(it.id) && JSON.stringify(remoteMap.get(it.id)) !== JSON.stringify(it),
    )

    if (toAdd.length === 0 && toRemove.length === 0 && toUpdate.length === 0) return

    applying = true
    // Aplicar cambios al Y.Array.
    toAdd.forEach((item) => view.push(item))
    toRemove.forEach((item) => view.remove(item.id))
    toUpdate.forEach((item) => view.update(item.id, item))
    queueMicrotask(() => {
      applying = false
    })
  }

  /** Sincroniza Y.Array → local: añade nuevos, borra quitados, actualiza modificados. */
  function syncRemoteToLocal(remoteItems: T[]) {
    if (applying) return
    const localIds = new Set(localItems.value.map((it) => it.id))
    const remoteIds = new Set(remoteItems.map((it) => it.id))

    const toAdd = remoteItems.filter((it) => !localIds.has(it.id))
    const removed = localItems.value.filter((it) => !remoteIds.has(it.id))

    if (toAdd.length === 0 && removed.length === 0) {
      // Verificar si hay modificaciones (mismos ids, contenido distinto).
      const localMap = new Map(localItems.value.map((it) => [it.id, it]))
      const remoteMap = new Map(remoteItems.map((it) => [it.id, it]))
      let changed = false
      for (const id of localIds) {
        const l = JSON.stringify(localMap.get(id))
        const r = JSON.stringify(remoteMap.get(id))
        if (l !== r) {
          changed = true
          break
        }
      }
      if (!changed) return
    }

    applying = true
    // SAFETY GUARD: si el remoto está vacío pero el local tiene datos, NO
    // reemplazar. Esto evita que un fallo temporal del canal Realtime
    // (CHANNEL_ERROR, timeout, RLS que bloquea la fila del Y.Doc) borre
    // los datos locales del usuario. El remoto vacío solo se aplica si
    // el local también está vacío (caso legítimo: usuario borró todo).
    if (remoteItems.length === 0 && localItems.value.length > 0) {
      console.warn(
        '[sync] remoto vacío pero local tiene datos — no se reemplaza ' +
        '(posible fallo del canal Realtime). Local preservado.',
      )
      applying = false
      return
    }
    // Reemplazo completo del local con la versión remota (fuente de verdad CRDT).
    localItems.value = remoteItems.slice()
    // El cambio vino del CRDT (remoto) → persistir en IndexedDB local,
    // que de otro modo no se enteraría (los stores solo persisten en
    // acciones explícitas del usuario).
    scheduleRemotePersist()
    queueMicrotask(() => {
      applying = false
    })
  }

  // --- Watchers (sin deep: solo reaccionan a reasignaciones) ---
  const stopLocal = watch(localItems, (newItems) => syncLocalToRemote(newItems))
  const stopRemote = watch(view.items, (remoteItems) => syncRemoteToLocal(remoteItems))

  // --- Reconciliación inicial: esperar al estado remoto ---
  await ctx.ready

  // Reconciliar: el remoto es la fuente de verdad (CRDT ya fusionó).
  // Pero preservamos lo local que el remoto no tenga (todavía no subido).
  const remoteIds = new Set(view.items.value.map((it) => it.id))
  const localOnly = localItems.value.filter((it) => !remoteIds.has(it.id))
  if (localOnly.length > 0) {
    localOnly.forEach((item) => view.push(item))
  }

  // Integrar lo remoto en el local.
  syncRemoteToLocal(view.items.value)

  // Marcar sync como activo: la colección local está cargada y reconciliada.
  // Aunque el canal Realtime falle (CHANNEL_ERROR), la app sigue siendo
  // funcional offline-first — los datos locales están disponibles y los
  // cambios se persisten localmente. El indicador "Sin sincronizar" solo
  // debe aparecer si realmente no hay datos locales cargados.
  syncConnected.value = true

  return {
    disconnect: () => {
      syncConnected.value = false
      stopLocal()
      stopRemote()
      if (persistTimer) clearTimeout(persistTimer)
    },
  }
}

export async function connectBookCollections(
  synced: SyncedDoc,
  collections: {
    highlights: Ref<Highlight[]>
    summaries: Ref<Summary[]>
    translations: Ref<Translation[]>
    bookmarks: Ref<Bookmark[]>
    highlightComments?: Ref<HighlightComment[]>
  },
  persist?: {
    highlights?: (items: Highlight[]) => Promise<void>
    summaries?: (items: Summary[]) => Promise<void>
    translations?: (items: Translation[]) => Promise<void>
    bookmarks?: (items: Bookmark[]) => Promise<void>
    highlightComments?: (items: HighlightComment[]) => Promise<void>
  },
) {
  const h = await connectCollection<Highlight>(
    collections.highlights, synced, 'highlights', persist?.highlights,
  )
  const s = await connectCollection<Summary>(
    collections.summaries, synced, 'summaries', persist?.summaries,
  )
  const t = await connectCollection<Translation>(
    collections.translations, synced, 'translations', persist?.translations,
  )
  const b = await connectCollection<Bookmark>(
    collections.bookmarks, synced, 'bookmarks', persist?.bookmarks,
  )
  // Comentarios sobre highlights (F2.1): opcional, sólo se conecta si el
  // caller pasa el Ref (libros compartidos). Viaja en el mismo Y.Doc.
  const c = collections.highlightComments
    ? await connectCollection<HighlightComment>(
        collections.highlightComments, synced, 'highlightComments', persist?.highlightComments,
      )
    : null
  return {
    disconnect: () => {
      h.disconnect()
      s.disconnect()
      t.disconnect()
      b.disconnect()
      c?.disconnect()
    },
  }
}
